import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { formatEUR } from './constants';

export default function CashModal({ open, remaining, onClose, onValidate }) {
    const [cashGiven, setCashGiven] = useState('');
    const [step, setStep] = useState('input'); // 'input' | 'confirm'
    const inputRef = useRef(null);
    const rem = Number(remaining) || 0;

    useEffect(() => {
        if (!open) return;
        setCashGiven(rem > 0 ? rem.toFixed(2) : '');
        setStep('input');
        setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 0);
    }, [open, rem]);

    const cashChange = useMemo(() => {
        const given = parseFloat(String(cashGiven).replace(',', '.'));
        if (!Number.isFinite(given)) return 0;
        return Math.max(0, Math.round((given - rem) * 100) / 100);
    }, [cashGiven, rem]);

    const canValidate = useMemo(() => {
        const given = parseFloat(String(cashGiven).replace(',', '.'));
        return Number.isFinite(given) && given >= rem && rem > 0.0001;
    }, [cashGiven, rem]);

    const handleValidateInput = () => {
        if (!canValidate) return;
        if (cashChange > 0) {
            setStep('confirm');
        } else {
            onValidate({
                cash_given:  parseFloat(String(cashGiven).replace(',', '.')),
                cash_change: 0,
            });
        }
    };

    const handleConfirm = () => {
        onValidate({
            cash_given:  parseFloat(String(cashGiven).replace(',', '.')),
            cash_change: cashChange,
        });
    };

    if (!open) return null;

    if (step === 'confirm') {
        return (
            <div className="selector-overlay">
                <div className="selector-window" style={{ maxWidth: 360, gap: 16, alignItems: 'center' }}>
                    <h3 className="panel-title" style={{ textAlign: 'center', fontSize: 16 }}>
                        Rendu monnaie
                    </h3>

                    <div style={{ textAlign: 'center', margin: '8px 0' }}>
                        <div className="text-muted" style={{ fontSize: 13, marginBottom: 6 }}>
                            Rendre au client
                        </div>
                        <div style={{ fontSize: 36, fontWeight: 'bold', color: '#4fabf7' }}>
                            {formatEUR(cashChange)}
                        </div>
                    </div>

                    <div className="payment-amount-row text-muted" style={{ width: '100%' }}>
                        <span>Espèces reçues</span>
                        <span>{formatEUR(parseFloat(String(cashGiven).replace(',', '.')))}</span>
                    </div>
                    <div className="payment-amount-row text-muted" style={{ width: '100%' }}>
                        <span>Montant dû</span>
                        <span>{formatEUR(rem)}</span>
                    </div>

                    <div className="action-row" style={{ gap: 8, width: '100%', marginTop: 8 }}>
                        <button className="pill-btn outline flex-1" onClick={() => setStep('input')}>
                            ← Retour
                        </button>
                        <button className="pill-btn blue flex-1" onClick={handleConfirm}>
                            Fait ✓
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="selector-overlay">
            <div className="selector-window" style={{ maxWidth: 360, gap: 12 }}>
                <h3 className="panel-title" style={{ textAlign: 'center', fontSize: 16 }}>
                    Encaissement espèces
                </h3>

                <div className="payment-amount-row bold">
                    <span>À encaisser :</span>
                    <span>{formatEUR(rem)}</span>
                </div>

                <label className="apply-label">Monnaie donnée</label>
                <input
                    ref={inputRef}
                    type="number"
                    step="0.01"
                    className="white-input"
                    placeholder="0.00"
                    value={cashGiven}
                    onChange={e => setCashGiven(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleValidateInput()}
                />

                <div className="payment-amount-row" style={{ marginTop: 4 }}>
                    <span className="text-muted">Monnaie à rendre :</span>
                    <span style={{ fontWeight: 'bold', color: cashChange > 0 ? '#4fabf7' : 'inherit' }}>
                        {formatEUR(cashChange)}
                    </span>
                </div>

                <div className="presets-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    {[5, 10, 20, 50].map(v => (
                        <button key={v} className="preset-btn" onClick={() => setCashGiven(v.toFixed(2))}>
                            {v}€
                        </button>
                    ))}
                </div>

                <div className="action-row" style={{ gap: 8 }}>
                    <button className="pill-btn outline flex-1" onClick={onClose}>Annuler</button>
                    <button
                        className="pill-btn blue flex-1"
                        disabled={!canValidate}
                        onClick={handleValidateInput}
                    >
                        {cashChange > 0 ? 'Suivant →' : 'Valider'}
                    </button>
                </div>
            </div>
        </div>
    );
}

CashModal.propTypes = {
    open:       PropTypes.bool.isRequired,
    remaining:  PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onClose:    PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired,
};